import type { FC } from 'react';

import Pill from '@/common-components/Pill';
import { COIN_RULE_ROWS } from '@/features/settings/constants';
import type { CoinRules } from '@/types/settings.types';

interface CoinRulesPanelProps {
  coinRules: CoinRules | undefined;
  version: number;
}

/**
 * Read-only by design.
 *
 * The server reprices the whole coin economy from `DEFAULT_COIN_RULES` whenever the coin-rules
 * version is bumped in a release. If this panel accepted edits, they would be silently wiped at
 * the next bump — so it shows the live values and says plainly where they come from.
 */
const CoinRulesPanel: FC<CoinRulesPanelProps> = ({ coinRules, version }) => (
  <section className="rounded-2xl border border-[var(--color-divider)] bg-white p-7 shadow-[var(--shadow-card)]">
    <header className="mb-1 flex items-center justify-between gap-3">
      <h2 className="text-[13px] font-extrabold text-[var(--color-ink)]">🪙 Coin Rules</h2>
      <Pill tone="neutral">v{version}</Pill>
    </header>
    <p className="mb-4 text-[12px] text-[var(--color-ink-muted)]">
      Release-managed and read-only. These values ship with the app and are repriced together when
      the coin economy is versioned, so they are not editable here.
    </p>

    {!coinRules ? (
      <p className="py-6 text-center text-[13px] text-[var(--color-ink-muted)]">Loading…</p>
    ) : (
      <div>
        {COIN_RULE_ROWS.map((row) => {
          const value = coinRules[row.key as keyof CoinRules];
          return (
            <div
              key={row.key}
              className="flex items-center justify-between gap-6 border-b border-[var(--color-divider)] py-3 last:border-b-0"
            >
              <span className="text-[13px] text-[var(--color-ink)]">{row.label}</span>
              <span className="shrink-0 text-[13px] font-bold text-[var(--color-ink)]">
                {value}
                {row.unit === '%' ? '%' : null}
                {row.unit === 'hours' ? ' hrs' : null}
                {row.unit === 'spend' ? ' coins' : null}
                {row.unit === undefined ? ' coins' : null}
              </span>
            </div>
          );
        })}
      </div>
    )}
  </section>
);

export default CoinRulesPanel;
